import React, { useEffect, useState } from 'react';
import './DashboardPage.scss';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Calendar } from 'primereact/calendar';
import { Nullable } from 'primereact/ts-helpers';
import { AdminMonitorStatsResponse } from '../../../shared/models/admin/dashboard/AdminMonitorStatsResponse';
import {
  getActiveMonitorStats,
  getActiveUsersStats,
  getConfiguration,
  getDailyRequestStats,
} from '../../../shared/services/admin/dashboard/AdminDashboardService';
import Loader from '../../../shared/components/common/loader/Loader';
import { AdminConfigurationResponse } from '../../../shared/models/admin/dashboard/AdminConfigurationResponse';
import ModelBox from './components/ModelBox';

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeMonitors, setActiveMonitors] = useState<number | null>(null);
  const [users, setUsers] = useState<{
    disabled: number;
    blocked: number;
    trial: number;
    paid: number;
    unpaid: number;
  } | null>(null);
  const [configuration, setConfiguration] = useState<AdminConfigurationResponse[] | null>(null);
  const [stats, setStats] = useState<AdminMonitorStatsResponse[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [initialLoading, setInitialLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);

  const findStats = (type: string) => stats.find((s) => s.monitorType === type) || null;
  const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  const loadStats = (date: Date) => {
    setStatsLoading(true);
    return getDailyRequestStats(format(date, 'yyyy-MM-dd')).then((data) => {
      setStats(Array.isArray(data) ? data : []);
      setStatsLoading(false);
    });
  };

  useEffect(() => {
    Promise.all([
      getActiveMonitorStats(),
      getActiveUsersStats(),
      getConfiguration(),
      getDailyRequestStats(format(new Date(), 'yyyy-MM-dd')),
    ]).then(([activeMonitorsData, usersData, configurationData, statsData]) => {
      setActiveMonitors(activeMonitorsData || 0);
      setUsers(usersData || { blocked: 0, trial: 0, disabled: 0, paid: 0, unpaid: 0 });
      setConfiguration(configurationData ?? []);
      setStats(Array.isArray(statsData) ? statsData : []);
      setInitialLoading(false);
    });
  }, []);

  if (initialLoading) {
    return (
      <div className="dashboard">
        <Loader />
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard__row">
        <div className="circle-data">
          <div className="circle-data__title">Paid users</div>
          <div className="circle-data__value">{users?.paid || 0}</div>
        </div>
        <div className="circle-data">
          <div className="circle-data__title">Trial users</div>
          <div className="circle-data__value">{users?.trial || 0}</div>
        </div>
        <div className="circle-data">
          <div className="circle-data__title">Blocked users</div>
          <div className="circle-data__value">{users?.blocked || 0}</div>
        </div>
      </div>
      <div className="dashboard__row">
        <div className="circle-data">
          <div className="circle-data__title">Disabled users</div>
          <div className="circle-data__value">{users?.disabled || 0}</div>
        </div>
        <div className="circle-data">
          <div className="circle-data__title">Payment problems</div>
          <div className="circle-data__value">{users?.unpaid || 0}</div>
        </div>
        <div className="circle-data">
          <div className="circle-data__title">Active monitors</div>
          <div className="circle-data__value">{activeMonitors}</div>
        </div>
      </div>

      <div className="dashboard__row dashboard__row--rect">
        <div className="rect-data">
          <div
            className={`rect-data__title ${
              configuration?.find((data) => data.feature === 'SEMRUSH')?.isEnabled
                ? 'rect-data__title--on'
                : 'rect-data__title--off'
            }`}
          >
            {t('semrushAPI')}
          </div>
        </div>
        <div className="rect-data">
          <div className={`rect-data__title rect-data__title--on`}>{t('gptApi')}</div>
        </div>
        <div className="rect-data">
          <div
            className={`rect-data__title ${
              configuration?.find((data) => data.feature === 'BROWSER')?.isEnabled
                ? 'rect-data__title--on'
                : 'rect-data__title--off'
            }`}
          >
            {t('gpt')}
          </div>
        </div>
      </div>

      <div className="dashboard__row dashboard__row--rect">
        <div className="rect-data">
          <div
            className={`rect-data__title ${
              configuration?.find((data) => data.feature === 'GEMINI')?.isEnabled
                ? 'rect-data__title--on'
                : 'rect-data__title--off'
            }`}
          >
            {t('geminiFlash')}
          </div>
        </div>
        <div className="rect-data">
          <div
            className={`rect-data__title ${
              configuration?.find((data) => data.feature === 'GEMINI_BROWSER')?.isEnabled
                ? 'rect-data__title--on'
                : 'rect-data__title--off'
            }`}
          >
            {t('geminiFlashPro')}
          </div>
          <div className="rect-data__value" />
        </div>
        <div className="rect-data">
          <div
            className={`rect-data__title ${
              configuration?.find((data) => data.feature === 'CLAUDE')?.isEnabled
                ? 'rect-data__title--on'
                : 'rect-data__title--off'
            }`}
          >
            {t('claudeApi')}
          </div>
        </div>
      </div>
      <div className="dashboard__row dashboard__row--rect">
        <div className="rect-data">
          <div
            className={`rect-data__title ${
              configuration?.find((data) => data.feature === 'BING')?.isEnabled
                ? 'rect-data__title--on'
                : 'rect-data__title--off'
            }`}
          >
            {t('bing')}
          </div>
        </div>
        <div className="rect-data">
          <div
            className={`rect-data__title ${
              configuration?.find((data) => data.feature === 'PROJECT_DAILY_STATS_MIGRATION')?.isEnabled
                ? 'rect-data__title--on'
                : 'rect-data__title--off'
            }`}
          >
            {t('projectDailyStatsMigration')}
          </div>
        </div>
        <div className="rect-data">
          <div
            className={`rect-data__title ${
              configuration?.find((data) => data.feature === 'PROJECT_MIGRATION')?.isEnabled
                ? 'rect-data__title--on'
                : 'rect-data__title--off'
            }`}
          >
            {t('statsMigration')}
          </div>
        </div>
      </div>

      <div className="dashboard__date-picker">
        <Calendar
          value={selectedDate}
          onChange={(e) => {
            const date = e.value as Nullable<Date>;
            if (date) {
              setSelectedDate(date);
              loadStats(date);
            }
          }}
          dateFormat="yy-mm-dd"
          maxDate={new Date()}
          readOnlyInput
          pt={{
            root: { className: 'calendar' },
            panel: { className: 'calendar-panel' },
          }}
        />
      </div>

      <div className={`dashboard__content${statsLoading ? ' dashboard__content--loading' : ''}`}>
        <ModelBox activeMonitors={activeMonitors} stats={findStats('GPT_API_BROWSER')} header="GPT 5 API Search" isToday={isToday} />
        <ModelBox activeMonitors={activeMonitors} stats={findStats('GPT_SEARCH_BROWSER')} header="GPT 5 Search" isToday={isToday} />
        <ModelBox activeMonitors={activeMonitors} stats={findStats('CLAUDE_API')} header="Claude Sonnet" isToday={isToday} />
        <ModelBox activeMonitors={activeMonitors} stats={findStats('DEEP_SEEK_API')} header="DeepSeek R1" isToday={isToday} />
        <ModelBox activeMonitors={activeMonitors} stats={findStats('PERPLEXITY_API')} header="Perplexity API Search" isToday={isToday} />
        <ModelBox activeMonitors={activeMonitors} stats={findStats('PERPLEXITY_SEARCH_BROWSER')} header="Perplexity Search" isToday={isToday} />
        <ModelBox activeMonitors={activeMonitors} stats={findStats('GROK_API')} header="Grok API" isToday={isToday} />
        <ModelBox activeMonitors={activeMonitors} stats={findStats('COPILOT_SEARCH_BROWSER')} header="Copilot" isToday={isToday} />
        <ModelBox activeMonitors={activeMonitors} stats={findStats('GEMINI_SEARCH_BROWSER')} header="Gemini Search" isToday={isToday} />
        <ModelBox activeMonitors={activeMonitors} stats={findStats('AI_OVERVIEW_NEW')} header="AI Overview" isToday={isToday} />
        <ModelBox activeMonitors={activeMonitors} stats={findStats('AI_MODE')} header="AI Mode" isToday={isToday} />
      </div>
    </div>
  );
};

export default DashboardPage;
